import { useState, useEffect, useCallback, useRef } from 'react';
import { PaginationState, SortingState, ColumnFiltersState } from '@tanstack/react-table';
import { useLocalStorage } from './useLocalStorage';
import { isDisabledTestValue } from '@/testFeatures';

interface FilteredDataTableState {
    columnFilters: ColumnFiltersState;
    sorting: SortingState;
    pagination: PaginationState;
}

interface UseFilteredDataTableStateProps {
    storageKey: string;
    defaultFilters?: ColumnFiltersState;
    defaultSorting?: SortingState;
    defaultPagination?: PaginationState;
    enabled?: boolean;
}

export interface UseFilteredDataTableState {
    columnFilters: ColumnFiltersState;
    setColumnFilters: (filters: ColumnFiltersState) => void;
    sorting: SortingState;
    setSorting: (sorting: SortingState) => void;
}

/**
 * Custom hook for persisting filtered table state to localStorage.
 *
 * @param {UseFilteredDataTableStateProps} props
 * @param {string} storageKey - Unique key for localStorage
 * @param {ColumnFiltersState} defaultFilters - Default column filters
 * @param {SortingState} defaultSorting - Default sorting state
 * @param {boolean} enabled - Whether to persist filtered table state to localStorage
 * @returns {UseFilteredDataTableState}
 */
export const useFilteredDataTableState = ({
    storageKey,
    defaultFilters = [],
    defaultSorting = [],
    enabled = true,
}: UseFilteredDataTableStateProps) => {
    const [savedState, setSavedState] = useLocalStorage<Partial<FilteredDataTableState>>(`filtered-table-${storageKey}`, {});
    const isInitializing = useRef(true);

    // Initialize state from localStorage or with defaults
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() => {
        // A stored test-only selection (for example "Max days (Test)") is dropped once the switch
        // is off, so the matching default below takes over instead of a filter the user cannot see.
        const savedFilters = savedState.columnFilters?.filter(filter => !isDisabledTestValue(filter.value));
        const initialFilters = enabled && savedFilters ? savedFilters : defaultFilters;

        // Ensure required filters from defaultFilters are always present
        const requiredFilters = defaultFilters.filter(filter => {
            return !initialFilters.some(f => f.id === filter.id);
        });

        return [...initialFilters, ...requiredFilters];
    });

    const [sorting, setSorting] = useState<SortingState>(() => {
        return enabled && savedState.sorting ? savedState.sorting : defaultSorting;
    });

    // Handle saving to localStorage
    const saveToStorage = useCallback(() => {
        if (!enabled || isInitializing.current) {
            isInitializing.current = false;
            return;
        }

        setSavedState({
            columnFilters,
            sorting,
        });
    }, [enabled, columnFilters, sorting, setSavedState]);

    // Save to localStorage when state changes with debounce
    useEffect(() => {
        if (!enabled) return;

        const timeoutId = setTimeout(saveToStorage, 100);
        return () => clearTimeout(timeoutId);
    }, [enabled, saveToStorage]);

    return {
        columnFilters,
        setColumnFilters,
        sorting,
        setSorting,
    };
};
